import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';

const pesos = (valor) => Number(valor || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
const numero = (valor) => Number(valor || 0).toLocaleString('es-AR', { maximumFractionDigits: 3 });
const hoy = () => new Date().toISOString().slice(0, 10);

export default function Reposicion() {
  const [pestana, setPestana] = useState('sugerencias');
  const [dias, setDias] = useState(30);
  const [sugerencias, setSugerencias] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [proveedorPedido, setProveedorPedido] = useState('');
  const [fechaEstimada, setFechaEstimada] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [itemsManual, setItemsManual] = useState([{ producto_id: '', cantidad_pedida: '', costo_estimado: '' }]);
  const [detalle, setDetalle] = useState(null);
  const [recepcion, setRecepcion] = useState({ items: {}, registrar_deuda: true, numero_comprobante: '', fecha_emision: hoy(), observaciones: '' });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargarBase = async () => {
    setCargando(true); setError('');
    try {
      const [prov, prods] = await Promise.all([api.get('/api/proveedores'), api.get('/api/precios/productos')]);
      setProveedores(Array.isArray(prov) ? prov.filter((p) => p.activo !== false) : []);
      setProductos(Array.isArray(prods) ? prods : []);
      await Promise.all([cargarSugerencias(), cargarPedidos()]);
    } catch (err) {
      setError(err instanceof ApiError ? err.mensaje : 'No se pudo cargar reposición y pedidos.');
    } finally { setCargando(false); }
  };

  const cargarSugerencias = async () => {
    const data = await api.get(`/api/reposicion/sugerencias?dias=${dias}&solo_reponer=true`);
    setSugerencias(Array.isArray(data.items) ? data.items : []);
  };
  const cargarPedidos = async () => {
    const data = await api.get('/api/pedidos-compra');
    setPedidos(Array.isArray(data) ? data : []);
  };

  useEffect(() => { cargarBase(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refrescarSugerencias = async () => {
    try { await cargarSugerencias(); } catch (err) { alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo recalcular.')); }
  };

  const seleccionados = useMemo(() => sugerencias.filter((s) => Number(seleccion[s.id] || 0) > 0), [sugerencias, seleccion]);
  const totalSeleccion = seleccionados.reduce((sum, item) => sum + Number(seleccion[item.id]) * Number(item.costo || 0), 0);

  const alternarSugerencia = (item, marcado) => {
    setSeleccion((prev) => ({ ...prev, [item.id]: marcado ? item.cantidad_sugerida : 0 }));
    if (marcado && !proveedorPedido && item.proveedor_id) setProveedorPedido(String(item.proveedor_id));
  };

  const crearPedido = async (items) => {
    if (!proveedorPedido) return alert('Seleccioná el proveedor del pedido.');
    if (!items.length) return alert('Agregá al menos un producto.');
    setGuardando(true);
    try {
      const data = await api.post('/api/pedidos-compra', {
        proveedor_id: Number(proveedorPedido),
        fecha_estimada: fechaEstimada || null,
        observaciones: observaciones.trim() || null,
        items,
      });
      alert(`✅ Pedido #${data.pedido_id} creado como Borrador.`);
      setSeleccion({}); setProveedorPedido(''); setFechaEstimada(''); setObservaciones('');
      setItemsManual([{ producto_id: '', cantidad_pedida: '', costo_estimado: '' }]);
      await Promise.all([cargarPedidos(), cargarSugerencias()]);
      setPestana('pedidos');
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo crear el pedido.'));
    } finally { setGuardando(false); }
  };

  const crearDesdeSugerencias = () => crearPedido(seleccionados.map((item) => ({
    producto_id: item.id,
    cantidad_pedida: Number(seleccion[item.id]),
    costo_estimado: Number(item.costo || 0),
  })));

  const crearManual = () => {
    const items = itemsManual.filter((i) => i.producto_id && Number(i.cantidad_pedida) > 0).map((i) => ({
      producto_id: Number(i.producto_id), cantidad_pedida: Number(i.cantidad_pedida), costo_estimado: Number(i.costo_estimado || 0),
    }));
    crearPedido(items);
  };

  const cambiarManual = (indice, campo, valor) => {
    setItemsManual((prev) => prev.map((item, i) => {
      if (i !== indice) return item;
      const siguiente = { ...item, [campo]: valor };
      if (campo === 'producto_id') {
        const prod = productos.find((p) => Number(p.id) === Number(valor));
        siguiente.costo_estimado = prod ? Number(prod.costo || prod.precio_costo || 0) : '';
      }
      return siguiente;
    }));
  };

  const abrirDetalle = async (id) => {
    try {
      const data = await api.get(`/api/pedidos-compra/${id}`);
      setDetalle(data);
      const items = {};
      (data.items || []).forEach((item) => {
        const pendiente = Number(item.cantidad_pendiente || 0);
        items[item.id] = {
          cantidad_recibida: pendiente > 0 ? pendiente : 0,
          costo_unitario: Number(item.costo_estimado || item.costo_actual || 0),
          actualizar_costo: true,
          actualizar_precio: false,
          lotes: item.maneja_lotes ? [{ numero_lote: '', fecha_elaboracion: '', fecha_vencimiento: '', cantidad: pendiente > 0 ? pendiente : 0 }] : [],
        };
      });
      setRecepcion({ items, registrar_deuda: true, numero_comprobante: '', fecha_emision: hoy(), observaciones: '' });
    } catch (err) { alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo abrir el pedido.')); }
  };

  const cambiarEstado = async (id, accion) => {
    const texto = accion === 'enviar' ? 'enviar al proveedor' : 'cancelar';
    if (!window.confirm(`¿Confirmás ${texto} el pedido #${id}?`)) return;
    try {
      await api.post(`/api/pedidos-compra/${id}/${accion}`, accion === 'cancelar' ? { motivo: 'Cancelado desde el módulo de pedidos' } : {});
      await cargarPedidos();
      if (detalle?.pedido?.id === id) await abrirDetalle(id);
    } catch (err) { alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo actualizar el pedido.')); }
  };

  const cambiarLoteRecepcion = (itemId, indice, campo, valor) => {
    setRecepcion((prev) => {
      const item = prev.items[itemId] || {};
      const lotes = [...(item.lotes || [])];
      lotes[indice] = { ...(lotes[indice] || {}), [campo]: valor };
      return { ...prev, items: { ...prev.items, [itemId]: { ...item, lotes } } };
    });
  };
  const agregarLoteRecepcion = (itemId) => setRecepcion((prev) => {
    const item = prev.items[itemId] || {};
    return { ...prev, items: { ...prev.items, [itemId]: { ...item, lotes: [...(item.lotes || []), { numero_lote: '', fecha_elaboracion: '', fecha_vencimiento: '', cantidad: '' }] } } };
  });
  const quitarLoteRecepcion = (itemId, indice) => setRecepcion((prev) => {
    const item = prev.items[itemId] || {};
    return { ...prev, items: { ...prev.items, [itemId]: { ...item, lotes: (item.lotes || []).filter((_, i) => i !== indice) } } };
  });

  const recibirPedido = async () => {
    if (!detalle?.pedido) return;
    const items = (detalle.items || []).filter((item) => Number(recepcion.items[item.id]?.cantidad_recibida || 0) > 0).map((item) => ({
      pedido_item_id: item.id,
      cantidad_recibida: Number(recepcion.items[item.id].cantidad_recibida),
      costo_unitario: Number(recepcion.items[item.id].costo_unitario || 0),
      actualizar_costo: Boolean(recepcion.items[item.id].actualizar_costo),
      actualizar_precio: Boolean(recepcion.items[item.id].actualizar_precio),
      lotes: item.maneja_lotes ? (recepcion.items[item.id].lotes || []).filter((l) => l.numero_lote && Number(l.cantidad) > 0).map((l) => ({
        numero_lote: l.numero_lote,
        fecha_elaboracion: l.fecha_elaboracion || null,
        fecha_vencimiento: l.fecha_vencimiento || null,
        cantidad: Number(l.cantidad),
      })) : [],
    }));
    if (!items.length) return alert('Indicá al menos una cantidad recibida.');
    setGuardando(true);
    try {
      const data = await api.post(`/api/pedidos-compra/${detalle.pedido.id}/recibir`, {
        items,
        registrar_deuda: recepcion.registrar_deuda,
        comprobante: recepcion.registrar_deuda ? {
          tipo_comprobante: 'X', numero_comprobante: recepcion.numero_comprobante || null, fecha_emision: recepcion.fecha_emision,
        } : null,
        observaciones: recepcion.observaciones || null,
      });
      alert(`✅ Recepción #${data.recepcion_id} registrada.${data.factura_compra_id ? `\nDeuda/factura #${data.factura_compra_id} creada.` : ''}`);
      await Promise.all([cargarPedidos(), cargarSugerencias()]);
      await abrirDetalle(detalle.pedido.id);
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo registrar la recepción.'));
    } finally { setGuardando(false); }
  };

  if (cargando) return <div style={{ padding: 30 }}>Cargando reposición…</div>;
  if (error) return <EstadoError mensaje={error} onReintentar={cargarBase} />;

  return <div style={panel}>
    <h2 style={{ margin: 0, color: '#0f172a' }}>📦 Reposición inteligente y pedidos</h2>
    <p style={{ color: '#64748b', marginTop: 5 }}>Calcula necesidades con stock, ventas, demora del proveedor y mercadería ya pedida.</p>
    <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: 20 }}>
      <Tab activo={pestana === 'sugerencias'} onClick={() => setPestana('sugerencias')}>⚡ Sugerencias</Tab>
      <Tab activo={pestana === 'manual'} onClick={() => setPestana('manual')}>➕ Pedido manual</Tab>
      <Tab activo={pestana === 'pedidos'} onClick={() => { setPestana('pedidos'); cargarPedidos(); }}>📋 Pedidos</Tab>
    </div>

    {pestana === 'sugerencias' && <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap', marginBottom: 15 }}>
        <label style={label}>Período de ventas
          <select value={dias} onChange={(e) => setDias(Number(e.target.value))} style={input}>
            <option value={7}>7 días</option><option value={30}>30 días</option><option value={60}>60 días</option><option value={90}>90 días</option>
          </select>
        </label>
        <button onClick={refrescarSugerencias} style={botonSecundario}>Recalcular</button>
        <span style={{ color: '#64748b' }}>{sugerencias.length} productos necesitan revisión</span>
      </div>
      <div style={{ overflowX: 'auto' }}><table style={tabla}>
        <thead><tr><Th></Th><Th>Producto</Th><Th>Proveedor</Th><Th>Stock</Th><Th>Mín./Máx.</Th><Th>Venta diaria</Th><Th>Cobertura</Th><Th>Pendiente</Th><Th>Sugerido</Th></tr></thead>
        <tbody>{sugerencias.map((item) => {
          const marcado = Number(seleccion[item.id] || 0) > 0;
          return <tr key={item.id} style={{ background: item.nivel === 'sin_stock' ? '#fef2f2' : item.nivel === 'bajo' ? '#fff7ed' : 'white' }}>
            <Td><input type="checkbox" checked={marcado} onChange={(e) => alternarSugerencia(item, e.target.checked)} /></Td>
            <Td><strong>{item.descripcion}</strong><br/><small>{item.codigo_barras || 'Sin código'}</small></Td>
            <Td>{item.proveedor_nombre}</Td><Td>{numero(item.cantidad_stock)}</Td>
            <Td>{numero(item.stock_minimo)} / {numero(item.stock_maximo)}</Td>
            <Td>{numero(item.venta_diaria)}</Td><Td>{item.cobertura_dias == null ? 'Sin ventas' : `${item.cobertura_dias} días`}</Td>
            <Td>{numero(item.cantidad_pendiente)}</Td>
            <Td><input type="number" min="0" step="0.001" value={seleccion[item.id] ?? item.cantidad_sugerida}
              onChange={(e) => setSeleccion((prev) => ({ ...prev, [item.id]: e.target.value }))} style={{ ...input, width: 95 }} /></Td>
          </tr>;
        })}</tbody>
      </table></div>
      {seleccionados.length > 0 && <PedidoCabecera proveedores={proveedores} proveedor={proveedorPedido} setProveedor={setProveedorPedido}
        fecha={fechaEstimada} setFecha={setFechaEstimada} observaciones={observaciones} setObservaciones={setObservaciones}
        total={totalSeleccion} onCrear={crearDesdeSugerencias} guardando={guardando} />}
    </>}

    {pestana === 'manual' && <>
      <div style={{ overflowX: 'auto' }}><table style={tabla}>
        <thead><tr><Th>Producto</Th><Th>Cantidad</Th><Th>Costo estimado</Th><Th>Subtotal</Th><Th></Th></tr></thead>
        <tbody>{itemsManual.map((item, indice) => <tr key={indice}>
          <Td><select value={item.producto_id} onChange={(e) => cambiarManual(indice, 'producto_id', e.target.value)} style={{ ...input, minWidth: 280 }}>
            <option value="">Seleccionar…</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.descripcion}</option>)}
          </select></Td>
          <Td><input type="number" min="0.001" step="0.001" value={item.cantidad_pedida} onChange={(e) => cambiarManual(indice, 'cantidad_pedida', e.target.value)} style={{ ...input, width: 110 }} /></Td>
          <Td><input type="number" min="0" step="0.01" value={item.costo_estimado} onChange={(e) => cambiarManual(indice, 'costo_estimado', e.target.value)} style={{ ...input, width: 130 }} /></Td>
          <Td>{pesos(Number(item.cantidad_pedida || 0) * Number(item.costo_estimado || 0))}</Td>
          <Td><button onClick={() => setItemsManual((prev) => prev.filter((_, i) => i !== indice))} style={botonPeligro}>×</button></Td>
        </tr>)}</tbody>
      </table></div>
      <button onClick={() => setItemsManual((prev) => [...prev, { producto_id: '', cantidad_pedida: '', costo_estimado: '' }])} style={botonSecundario}>＋ Agregar producto</button>
      <PedidoCabecera proveedores={proveedores} proveedor={proveedorPedido} setProveedor={setProveedorPedido}
        fecha={fechaEstimada} setFecha={setFechaEstimada} observaciones={observaciones} setObservaciones={setObservaciones}
        total={itemsManual.reduce((s, i) => s + Number(i.cantidad_pedida || 0) * Number(i.costo_estimado || 0), 0)}
        onCrear={crearManual} guardando={guardando} />
    </>}

    {pestana === 'pedidos' && <div style={{ overflowX: 'auto' }}><table style={tabla}>
      <thead><tr><Th>N.º</Th><Th>Fecha</Th><Th>Proveedor</Th><Th>Estado</Th><Th>Ítems</Th><Th>Recibido</Th><Th>Estimado</Th><Th>Acciones</Th></tr></thead>
      <tbody>{pedidos.map((p) => <tr key={p.id}>
        <Td>#{p.id}</Td><Td>{new Date(`${p.fecha_pedido}T12:00:00`).toLocaleDateString('es-AR')}</Td><Td><strong>{p.proveedor_nombre}</strong></Td>
        <Td><Estado estado={p.estado} /></Td><Td>{p.cantidad_items}</Td><Td>{numero(p.unidades_recibidas)} / {numero(p.unidades_pedidas)}</Td>
        <Td>{pesos(p.total_estimado)}</Td><Td><div style={{ display: 'flex', gap: 5 }}>
          <button onClick={() => abrirDetalle(p.id)} style={botonSecundario}>Ver</button>
          {p.estado === 'Borrador' && <button onClick={() => cambiarEstado(p.id, 'enviar')} style={botonPrimario}>Enviar</button>}
          {['Borrador','Enviado'].includes(p.estado) && Number(p.unidades_recibidas || 0) === 0 && <button onClick={() => cambiarEstado(p.id, 'cancelar')} style={botonPeligro}>Cancelar</button>}
        </div></Td>
      </tr>)}</tbody>
    </table></div>}

    {detalle && <Modal onCerrar={() => setDetalle(null)}>
      <h3 style={{ marginTop: 0 }}>Pedido #{detalle.pedido.id} — {detalle.pedido.proveedor_nombre}</h3>
      <Estado estado={detalle.pedido.estado} />
      <div style={{ overflowX: 'auto', marginTop: 12 }}><table style={tabla}>
        <thead><tr><Th>Producto</Th><Th>Pedido</Th><Th>Recibido</Th><Th>Pendiente</Th><Th>Recibir ahora</Th><Th>Costo</Th><Th>Actualizar</Th><Th>Lote y vencimiento</Th></tr></thead>
        <tbody>{detalle.items.map((item) => <tr key={item.id}>
          <Td><strong>{item.descripcion}</strong></Td><Td>{numero(item.cantidad_pedida)}</Td><Td>{numero(item.cantidad_recibida)}</Td><Td>{numero(item.cantidad_pendiente)}</Td>
          <Td><input type="number" min="0" max={item.cantidad_pendiente} step="0.001" disabled={Number(item.cantidad_pendiente) <= 0}
            value={recepcion.items[item.id]?.cantidad_recibida ?? 0} onChange={(e) => setRecepcion((prev) => ({ ...prev, items: { ...prev.items, [item.id]: { ...prev.items[item.id], cantidad_recibida: e.target.value } } }))} style={{ ...input, width: 100 }} /></Td>
          <Td><input type="number" min="0" step="0.01" value={recepcion.items[item.id]?.costo_unitario ?? 0}
            onChange={(e) => setRecepcion((prev) => ({ ...prev, items: { ...prev.items, [item.id]: { ...prev.items[item.id], costo_unitario: e.target.value } } }))} style={{ ...input, width: 110 }} /></Td>
          <Td><label><input type="checkbox" checked={Boolean(recepcion.items[item.id]?.actualizar_costo)} onChange={(e) => setRecepcion((prev) => ({ ...prev, items: { ...prev.items, [item.id]: { ...prev.items[item.id], actualizar_costo: e.target.checked, actualizar_precio: e.target.checked ? prev.items[item.id].actualizar_precio : false } } }))} /> costo</label><br/>
            <label><input type="checkbox" checked={Boolean(recepcion.items[item.id]?.actualizar_precio)} onChange={(e) => setRecepcion((prev) => ({ ...prev, items: { ...prev.items, [item.id]: { ...prev.items[item.id], actualizar_precio: e.target.checked, actualizar_costo: e.target.checked ? true : prev.items[item.id].actualizar_costo } } }))} /> precio</label></Td>
          <Td>{item.maneja_lotes ? <div style={{ minWidth: 360 }}>
            {(recepcion.items[item.id]?.lotes || []).map((lote, indice) => <div key={indice} style={{ display: 'grid', gridTemplateColumns: '1fr 105px 125px 32px', gap: 5, marginBottom: 5 }}>
              <input style={input} placeholder="N.º lote" value={lote.numero_lote || ''} onChange={(e) => cambiarLoteRecepcion(item.id, indice, 'numero_lote', e.target.value)} />
              <input style={input} type="number" min="0.001" step="0.001" placeholder="Cantidad" value={lote.cantidad ?? ''} onChange={(e) => cambiarLoteRecepcion(item.id, indice, 'cantidad', e.target.value)} />
              <input style={input} type="date" title="Vencimiento" value={lote.fecha_vencimiento || ''} onChange={(e) => cambiarLoteRecepcion(item.id, indice, 'fecha_vencimiento', e.target.value)} />
              <button type="button" style={{ ...botonPeligro, padding: 4 }} onClick={() => quitarLoteRecepcion(item.id, indice)}>×</button>
            </div>)}
            <button type="button" style={{ ...botonSecundario, padding: '5px 8px' }} onClick={() => agregarLoteRecepcion(item.id)}>＋ otro lote</button>
          </div> : <span style={{ color: '#64748b' }}>No controla lotes</span>}</Td>
        </tr>)}</tbody>
      </table></div>
      {['Enviado','Parcial'].includes(detalle.pedido.estado) && <div style={{ ...caja, marginTop: 15 }}>
        <label style={{ fontWeight: 'bold' }}><input type="checkbox" checked={recepcion.registrar_deuda} onChange={(e) => setRecepcion({ ...recepcion, registrar_deuda: e.target.checked })} /> Registrar la recepción como deuda/comprobante del proveedor</label>
        {recepcion.registrar_deuda && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <label style={label}>Número de comprobante (opcional)<input value={recepcion.numero_comprobante} onChange={(e) => setRecepcion({ ...recepcion, numero_comprobante: e.target.value })} style={input} /></label>
          <label style={label}>Fecha<input type="date" value={recepcion.fecha_emision} onChange={(e) => setRecepcion({ ...recepcion, fecha_emision: e.target.value })} style={input} /></label>
        </div>}
        <textarea value={recepcion.observaciones} onChange={(e) => setRecepcion({ ...recepcion, observaciones: e.target.value })} placeholder="Observaciones de la recepción" style={{ ...input, width: '100%', marginTop: 10 }} />
        <button onClick={recibirPedido} disabled={guardando} style={{ ...botonPrimario, marginTop: 10 }}>{guardando ? 'Registrando…' : '✅ Registrar recepción'}</button>
      </div>}
      {(detalle.recepciones || []).length > 0 && <div style={{ marginTop: 18 }}><h4>Historial de recepciones</h4>{detalle.recepciones.map((r) => <div key={r.id} style={{ ...caja, marginBottom: 8 }}>
        <strong>Recepción #{r.id}</strong> — {new Date(r.recibido_en).toLocaleString('es-AR')} — {pesos(r.total_recepcion)} {r.factura_compra_id ? `— deuda/factura #${r.factura_compra_id}` : '— sin deuda'}
      </div>)}</div>}
    </Modal>}
  </div>;
}

function PedidoCabecera({ proveedores, proveedor, setProveedor, fecha, setFecha, observaciones, setObservaciones, total, onCrear, guardando }) {
  return <div style={{ ...caja, marginTop: 18 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) 180px minmax(240px,1fr)', gap: 10 }}>
      <label style={label}>Proveedor<select value={proveedor} onChange={(e) => setProveedor(e.target.value)} style={input}><option value="">Seleccionar…</option>{proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></label>
      <label style={label}>Entrega estimada<input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={input} /></label>
      <label style={label}>Observaciones<input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} style={input} /></label>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}><strong>Total estimado: {pesos(total)}</strong><button onClick={onCrear} disabled={guardando} style={botonPrimario}>{guardando ? 'Guardando…' : 'Crear pedido'}</button></div>
  </div>;
}
function Tab({ activo, onClick, children }) { return <button onClick={onClick} style={{ padding: '10px 18px', border: 'none', borderBottom: activo ? '3px solid #2563eb' : '3px solid transparent', background: 'transparent', color: activo ? '#2563eb' : '#64748b', fontWeight: 'bold', cursor: 'pointer' }}>{children}</button>; }
function Estado({ estado }) { const colores = { Borrador: ['#f1f5f9','#475569'], Enviado: ['#dbeafe','#1d4ed8'], Parcial: ['#fef3c7','#b45309'], Recibido: ['#dcfce7','#15803d'], Cancelado: ['#fee2e2','#b91c1c'] }; const [bg,color] = colores[estado] || colores.Borrador; return <span style={{ background: bg, color, padding: '4px 9px', borderRadius: 20, fontWeight: 'bold', fontSize: 12 }}>{estado}</span>; }
function Modal({ onCerrar, children }) { return <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.65)', zIndex: 1000, overflowY: 'auto', padding: 25 }}><div style={{ background: 'white', borderRadius: 12, maxWidth: 1150, margin: '0 auto', padding: 24 }}><div style={{ textAlign: 'right' }}><button onClick={onCerrar} style={botonPeligro}>Cerrar</button></div>{children}</div></div>; }
const Th = ({ children }) => <th style={{ padding: 9, textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#64748b', whiteSpace: 'nowrap' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: 9, borderBottom: '1px solid #e2e8f0' }}>{children}</td>;
const panel = { padding: 20, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', minHeight: '80vh' };
const caja = { padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 };
const tabla = { width: '100%', borderCollapse: 'collapse', minWidth: 760 };
const input = { display: 'block', width: '100%', boxSizing: 'border-box', padding: 8, border: '1px solid #cbd5e1', borderRadius: 5, marginTop: 4 };
const label = { display: 'block', color: '#475569', fontSize: 13, fontWeight: 'bold' };
const botonPrimario = { padding: '8px 13px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' };
const botonSecundario = { padding: '8px 13px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' };
const botonPeligro = { padding: '7px 10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' };
